import { httpGet, httpPost, httpPatchMerge, httpDelete } from './http'
import type { TEntityId } from '~/types/models'
import type { ITsdSupplier } from '~/types/tsd/models'
import { getTsdSuppliersBySupplierId } from '~/services/suppliers'
import { getTsds, getTsdSuppliersByTsdId } from '~/services/tsds'
import type { ITsd } from '~/types/tsd/models'

const apiEntityPath = '/api/tsdsuppliers'

//
// A TSD<->Supplier join is unique: a partner that already has a tsdSupplier row for
// this parent must not be offerable again. The pickers use these to grey out the
// options that are taken (see modules/tsdSupplier.ts and modules/supplierTsd.ts).
//

/** Ids of the suppliers already joined to this TSD, whatever their status. */
export async function getJoinedSupplierIdsForTsd(
  tsdId?: TEntityId
): Promise<Set<TEntityId>> {
  if (!tsdId) {
    // No parent — grey out nothing rather than break the picker. Neither card can
    // actually reach this; both overview pages always supply the parent id.
    return new Set()
  }
  const joined = await getTsdSuppliersByTsdId(tsdId)
  return new Set(
    (joined ?? [])
      .map((tsdSupplier) => tsdSupplier.supplier?.id)
      .filter(Boolean) as TEntityId[]
  )
}

/** Ids of the TSDs already joined to this supplier, whatever their status. */
export async function getJoinedTsdIdsForSupplier(
  supplierId?: TEntityId
): Promise<Set<TEntityId>> {
  if (!supplierId) {
    return new Set()
  }
  const joined = await getTsdSuppliersBySupplierId(supplierId)
  return new Set(
    (joined ?? [])
      .map((tsdSupplier) => tsdSupplier.tsd?.id)
      .filter(Boolean) as TEntityId[]
  )
}

/**
 * The TSDs that handle this supplier -- those with a tsdSupplier row for it,
 * whatever its status -- as FULL TSD records, in getTsds' order.
 *
 * Full records, not the join's `{ id, label }`: they become an msService's
 * `tsd`, which the API types as a whole Tsd and which the kit roll-up reads
 * `descr` from (KitItemServices). The Add Services dialogs' TSD select.
 */
export async function getTsdsHandlingSupplier(
  supplierId: TEntityId
): Promise<ITsd[]> {
  const [joined, tsds] = await Promise.all([
    getTsdSuppliersBySupplierId(supplierId),
    getTsds(),
  ])
  const tsdIds = new Set(
    (joined ?? []).map((tsdSupplier) => tsdSupplier.tsd?.id).filter(Boolean)
  )
  return (tsds ?? []).filter((tsd) => tsdIds.has(tsd.id))
}

export async function getTsdSupplierOpportunityRegistrationEmailAddresses(
  id: string
) {
  const callingMn = getTsdSupplierOpportunityRegistrationEmailAddresses.name
  const apiPath = `${apiEntityPath}/${id}/opportunityregistrationemailaddresses`
  return httpGet<string[]>({ callingMn, apiPath })
}

export async function createTsdSupplier(entity: ITsdSupplier) {
  const callingMn = createTsdSupplier.name
  const apiPath = apiEntityPath
  return httpPost<ITsdSupplier>({ callingMn, apiPath, entity })
}

export async function updateTsdSupplier(
  entityData: Partial<ITsdSupplier>,
  id: TEntityId
) {
  const callingMn = updateTsdSupplier.name
  const apiPath = `${apiEntityPath}/${id}`
  type TPartialITsdSupplier = Partial<ITsdSupplier>
  return httpPatchMerge<TPartialITsdSupplier>({
    callingMn,
    apiPath,
    entityData,
  })
}

export async function deleteTsdSupplier(entityData: ITsdSupplier) {
  const callingMn = deleteTsdSupplier.name
  const apiPath = apiEntityPath
  return httpDelete<ITsdSupplier>({
    callingMn,
    apiPath,
    entityData,
  })
}
